import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import adminAxios from "../../../api/adminAxios"; // ✅ adjust path if needed

const SellerDetailsPage = () => {
  const { sellerId } = useParams();

  const [seller, setSeller] = useState(null);

  // ✅ products can come from seller.products OR data.products
  const [products, setProducts] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ✅ pagination
  const [page, setPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    let mounted = true;

    const fetchSeller = async () => {
      setLoading(true);
      setError("");

      try {
        const { data } = await adminAxios.get(`/api/admin/sellers/${sellerId}`);

        if (!mounted) return;

        const sellerObj = data?.seller || data;

        // ✅ products sometimes separate
        const list =
          (Array.isArray(sellerObj?.products) && sellerObj.products) ||
          (Array.isArray(data?.products) && data.products) ||
          (Array.isArray(data?.seller?.products) && data.seller.products) ||
          [];

        setSeller(sellerObj || null);
        setProducts(list);
        console.log("SellerDetails API data:", data);
console.log("First product:", list?.[0]);

        // reset to page 1 when seller changes
        setPage(1);
      } catch (err) {
        if (!mounted) return;

        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Error fetching seller details";
        setError(msg);
        setSeller(null);
        setProducts([]);
        console.error("Error fetching seller details:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    if (sellerId) fetchSeller();
    else {
      setLoading(false);
      setError("Seller id missing in URL");
    }

    return () => {
      mounted = false;
    };
  }, [sellerId]);

  // ✅ address object -> readable string
  const addressText = useMemo(() => {
    const a = seller?.address;
    if (!a) return "-";
    if (typeof a === "string") return a;

    if (typeof a === "object") {
      const parts = [
        a?.street,
        a?.city,
        a?.state,
        a?.country,
        a?.postalCode,
        a?.pincode,
      ].filter(Boolean);
      return parts.length ? parts.join(", ") : "-";
    }
    return "-";
  }, [seller]);

  // ✅ helper: safely extract "Price"
  const getPrice = (p) => {
    // common patterns
    const v0 = Array.isArray(p?.variants) ? p.variants[0] : null;

    return (
      p?.price ??
      p?.sellingPrice ??
      p?.offerPrice ??
      p?.mrp ??
      p?.salePrice ??
      p?.priceRange?.min ??
      p?.priceRange?.max ??
      v0?.price ??
      v0?.sellingPrice ??
      "-"
    );
  };

  // ✅ helper: safely extract "Stock"
  const getStock = (p) => {
    const v0 = Array.isArray(p?.variants) ? p.variants[0] : null;

    return (
      p?.stock ??
      p?.quantity ??
      p?.totalStock ??
      p?.availableStock ??
      v0?.stock ??
      v0?.quantity ??
      "-"
    );
  };

  // ✅ helper: category name
  const getCategory = (p) => {
    return (
      p?.category?.name ??
      p?.category?.title ??
      p?.categoryName ??
      p?.category ??
      "-"
    );
  };

  // ✅ helper: sub category name
  const getSubCategory = (p) => {
    return (
      p?.subCategory?.name ??
      p?.subCategory?.title ??
      p?.subcategory?.name ??
      p?.subCategoryName ??
      p?.subCategory ??
      p?.subcategory ??
      "-"
    );
  };

  // ✅ helper: delivery preference
  const getDeliveryPreference = (p) => {
    return (
      p?.deliveryPreference ??
      p?.deliveryType ??
      p?.shippingPreference ??
      seller?.deliveryPreference ??
      "-"
    );
  };

  // ✅ helper: sale mode
  const getSaleMode = (p) => {
    return (
      p?.saleMode ??
      p?.modeOfSale ??
      p?.sellType ??
      p?.rental?.enabled
        ? "Rental"
        : undefined ??
      "-"
    );
  };

  // ✅ pagination derived
  const totalProducts = products.length;
  const totalPages = Math.max(1, Math.ceil(totalProducts / pageSize));
  const safePage = Math.min(Math.max(page, 1), totalPages);

  const paginatedProducts = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return products.slice(start, start + pageSize);
  }, [products, safePage]);

  const visibilityLevel =
    seller?.Visibilitylevel ??
    seller?.visibilityLevel ??
    seller?.visibilitylevel ??
    "-";

  const paymentDate = seller?.paymentDate
    ? new Date(seller.paymentDate).toLocaleString()
    : "-";

  const goPrev = () => setPage((p) => Math.max(1, p - 1));
  const goNext = () => setPage((p) => Math.min(totalPages, p + 1));

  if (loading) return <div className="p-4">Loading...</div>;
  if (error) return <div className="p-4 text-red-600">Error: {error}</div>;
  if (!seller) return <div className="p-4">Seller not found.</div>;

  return (
    <div className="min-h-screen bg-gray-100 px-4 py-8">
      <div className="bg-white shadow-lg rounded-lg p-8 max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-800 mb-6 text-center">
          Seller Details
        </h1>

        {/* Seller Details */}
        <div className="mb-8">
          <ul className="text-gray-700 space-y-1">
            <li>
              <strong>Seller Name:</strong>{" "}
              {seller?.sellerName || seller?.name || "-"}
            </li>
            <li>
              <strong>Email:</strong> {seller?.email || "-"}
            </li>
            <li>
              <strong>Mobile:</strong> {seller?.mobile || seller?.phone || "-"}
            </li>
            <li>
              <strong>Address:</strong> {addressText}
            </li>
            <li>
              <strong>City:</strong>{" "}
              {seller?.city || seller?.address?.city || "-"}
            </li>
            <li>
              <strong>State:</strong>{" "}
              {seller?.state || seller?.address?.state || "-"}
            </li>
            <li>
              <strong>Country:</strong>{" "}
              {seller?.country || seller?.address?.country || "-"}
            </li>
            <li>
              <strong>Pincode:</strong>{" "}
              {seller?.pincode ||
                seller?.address?.postalCode ||
                seller?.address?.pincode ||
                "-"}
            </li>
            <li>
              <strong>GST Number:</strong>{" "}
              {seller?.gstNumber || seller?.gst || "-"}
            </li>
            <li>
              <strong>Visibility Level:</strong> {visibilityLevel}
            </li>
            <li>
              <strong>Payment Date:</strong> {paymentDate}
            </li>
          </ul>
        </div>

        {/* Product Details */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-2xl font-bold text-gray-800">Product Details</h2>
          <div className="text-sm text-gray-600">
            Total: <span className="font-semibold">{totalProducts}</span>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="table-auto w-full border-collapse border border-gray-300">
            <thead className="bg-gray-200">
              <tr>
                <th className="border border-gray-300 px-4 py-2">Product Name</th>
                <th className="border border-gray-300 px-4 py-2">Price</th>
                <th className="border border-gray-300 px-4 py-2">Stock</th>
                <th className="border border-gray-300 px-4 py-2">Category</th>
                <th className="border border-gray-300 px-4 py-2">Sub Category</th>
                <th className="border border-gray-300 px-4 py-2">
                  Delivery Preference
                </th>
                <th className="border border-gray-300 px-4 py-2">Sale Mode</th>
              </tr>
            </thead>

            <tbody>
              {paginatedProducts.length > 0 ? (
                paginatedProducts.map((product, index) => (
                  <tr
                    key={product?._id || `${safePage}-${index}`}
                    className="hover:bg-gray-100"
                  >
                    <td className="border border-gray-300 px-4 py-2">
                      {product?.productName || product?.name || "-"}
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      {getPrice(product)}
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      {getStock(product)}
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      {getCategory(product)}
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      {getSubCategory(product)}
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      {getDeliveryPreference(product)}
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      {getSaleMode(product)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="7"
                    className="border border-gray-300 px-4 py-2 text-center"
                  >
                    No products found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ✅ Pagination Controls */}
        {totalProducts > pageSize && (
          <div className="flex items-center justify-between mt-4">
            <button
              onClick={goPrev}
              disabled={safePage === 1}
              className={`px-4 py-2 rounded border ${
                safePage === 1
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50"
              }`}
            >
              Prev
            </button>

            <div className="text-sm text-gray-700">
              Page <span className="font-semibold">{safePage}</span> of{" "}
              <span className="font-semibold">{totalPages}</span>
            </div>

            <button
              onClick={goNext}
              disabled={safePage === totalPages}
              className={`px-4 py-2 rounded border ${
                safePage === totalPages
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50"
              }`}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SellerDetailsPage;




























// import React, { useEffect, useState } from "react";
// import { useParams } from "react-router-dom";
// import axios from "axios";
// import { baseurl } from "../../config/url";

// const SellerDetailsPage = () => { // Component name starts with an uppercase letter
//   const { sellerId } = useParams(); // Get seller ID from the URL
//   const [seller, setSeller] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   // Fetch seller and product details
//   useEffect(() => {
//     const fetchSeller = async () => {
//       try {
//         const sellerId = localStorage.getItem("sellerId");
//         const response = await axios.get(`${baseurl}seller/${sellerId}`);  
//         setSeller(response.data);
//       } catch (err) {
//         setError(err.response ? err.response.data.message : "Error fetching seller details");
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchSeller();
//   }, [sellerId]);

//   if (loading) return <div>Loading...</div>;
//   if (error) return <div>Error: {error}</div>;

//   return (
//     <div className="min-h-screen bg-gray-100 px-4 py-8">
//       <div className="bg-white shadow-lg rounded-lg p-8 max-w-5xl mx-auto">
//         <h1 className="text-3xl font-bold text-gray-800 mb-6 text-center">Seller Details</h1>

//         {/* Seller Details */}
//         <div className="mb-8">
//           <ul className="text-gray-700">
//             <li><strong>Seller Name:</strong> {seller.sellerName}</li>
//             <li><strong>Email:</strong> {seller.email}</li>
//             <li><strong>Mobile:</strong> {seller.mobile}</li>
//             <li><strong>Address:</strong> {seller.address}</li>
//             <li><strong>City:</strong> {seller.city}</li>
//             <li><strong>State:</strong> {seller.state}</li>
//             <li><strong>Country:</strong> {seller.country}</li>
//             <li><strong>Pincode:</strong> {seller.pincode}</li>
//             <li><strong>GST Number:</strong> {seller.gstNumber}</li>
//             <li><strong>Visibility Level:</strong> {seller.Visibilitylevel}</li>
//             <li><strong>Payment Date:</strong> {seller.paymentDate}</li>
//           </ul>
//         </div>

//         {/* Product Details Table */}
//         <h2 className="text-2xl font-bold text-gray-800 mb-4">Product Details</h2>
//         <div className="overflow-x-auto">
//           <table className="table-auto w-full border-collapse border border-gray-300">
//             <thead className="bg-gray-200">
//               <tr>
//                 <th className="border border-gray-300 px-4 py-2">Product Name</th>
//                 <th className="border border-gray-300 px-4 py-2">Price</th>
//                 <th className="border border-gray-300 px-4 py-2">Stock</th>
//                 <th className="border border-gray-300 px-4 py-2">Category</th>
//                 <th className="border border-gray-300 px-4 py-2">Sub Category</th>
//                 <th className="border border-gray-300 px-4 py-2">Delivery Preference</th>
//                 <th className="border border-gray-300 px-4 py-2">Sale Mode</th>
//               </tr>
//             </thead>
//             <tbody>
//               {seller.products && seller.products.length > 0 ? (
//                 seller.products.map((product, index) => (
//                   <tr key={index} className="hover:bg-gray-100">
//                     <td className="border border-gray-300 px-4 py-2">{product.productName}</td>
//                     <td className="border border-gray-300 px-4 py-2">{product.price}</td>
//                     <td className="border border-gray-300 px-4 py-2">{product.stock}</td>
//                     <td className="border border-gray-300 px-4 py-2">{product.category}</td>
//                     <td className="border border-gray-300 px-4 py-2">{product.subCategory}</td>
//                     <td className="border border-gray-300 px-4 py-2">{product.deliveryPreference}</td>
//                     <td className="border border-gray-300 px-4 py-2">{product.saleMode}</td>
//                   </tr>
//                 ))
//               ) : (
//                 <tr>
//                   <td colSpan="7" className="border border-gray-300 px-4 py-2 text-center">
//                     No products found.
//                   </td>
//                 </tr>
//               )}
//             </tbody>
//           </table>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default SellerDetailsPage;
